import { useAuth } from 'react-oidc-context';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useStore } from '../stores/store';
import { AppRoute } from '../constants/route';
import {
  generateLocaleRoute,
  isParcelCreationRoute,
} from '../utils/uri/uri.utils';

const getOidcUserStorageKey = (authority?: string, clientId?: string) => {
  if (!authority || !clientId) return null;
  return `oidc.user:${authority}:${clientId}`;
};

export const useOuathTokens = () => {
  const {
    userStore: {
      setLoginData,
      setSignoutRedirect,
      getCurrentUser,
      getCurrentUserGuestMode,
      user,
      isLoadingUser,
      loginGup,
    },
    commonStore: {
      accessToken,
      refreshToken,
      guestAccessToken,
      guestRefreshToken,
      isGuestParcelCreation,
      setAppLoaded,
      toastError,
      resetAppState,
    },
    localizationsStore: { selectedCountry, selectedLocalization },
  } = useStore();

  const navigate = useNavigate();
  const location = useLocation();

  const auth = useAuth();
  const signInRoute = generateLocaleRoute(
    `${AppRoute.AUTH}/${AppRoute.SIGNIN}`
  );

  useEffect(() => {
    // Locale check is required so we are sure that country and lang will be used in headers of /profile API call.
    if (
      isGuestParcelCreation ||
      !selectedCountry ||
      !selectedLocalization ||
      isLoadingUser
    )
      return;

    const initViaOuath = async () => {
      if (
        auth.isAuthenticated &&
        auth.user?.access_token &&
        auth.user?.refresh_token
      ) {
        setLoginData({
          accessToken: auth.user.access_token,
          refreshToken: auth.user.refresh_token,
        });
        setSignoutRedirect(auth.signoutRedirect);

        if (!user) await getCurrentUser();
      }

      setAppLoaded(true);
    };
    initViaOuath();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    auth,
    selectedCountry,
    selectedLocalization,
    setSignoutRedirect,
    setLoginData,
    setAppLoaded,
  ]);

  useEffect(() => {
    if (!isGuestParcelCreation) return;

    const initViaRestOld = async () => {
      if (guestAccessToken && !user) {
        await getCurrentUserGuestMode();
      }

      setAppLoaded(true);
    };
    initViaRestOld();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setAppLoaded, user, guestAccessToken, guestRefreshToken]);

  useEffect(() => {
    const oidcUserStorageKey = getOidcUserStorageKey(
      auth.settings.authority,
      auth.settings.client_id
    );

    if (!oidcUserStorageKey) return;

    const syncAuthAcrossTabs = async (event: StorageEvent) => {
      if (event.storageArea !== window.localStorage) return;
      if (event.key !== oidcUserStorageKey) return;

      if (event.newValue) {
        if (!auth.isAuthenticated && !auth.activeNavigator) {
          try {
            await auth.signinSilent();
          } catch {
            // Ignore cross-tab sync failures. Route-level auth handling remains the fallback.
          }
        }
        return;
      }

      if (!auth.isAuthenticated && !accessToken && !refreshToken && !user) {
        return;
      }

      resetAppState();
      await auth.removeUser().catch(() => undefined);
      navigate(signInRoute, { replace: true });
    };

    window.addEventListener('storage', syncAuthAcrossTabs);

    return () => {
      window.removeEventListener('storage', syncAuthAcrossTabs);
    };
  }, [
    accessToken,
    auth,
    navigate,
    refreshToken,
    resetAppState,
    signInRoute,
    user,
  ]);

  useEffect(() => {
    if (auth.error) {
      const isTokenRefreshError =
        auth.error.source === 'renewSilent' ||
        auth.error.source === 'signinSilent' ||
        auth.error.message === 'invalid_grant';

      if (isTokenRefreshError) {
        const discardExpiredSession = async () => {
          const redirectUri = `${location.pathname}${location.search}`;
          const preserveParcelCreationState =
            isParcelCreationRoute(redirectUri);

          auth.stopSilentRenew();
          resetAppState({ preserveParcelCreationState });

          await auth.removeUser().catch(() => undefined);
          await auth.clearStaleState().catch(() => undefined);
          await loginGup({ auth, redirectUri });
        };

        discardExpiredSession();
        return;
      }

      toastError(auth.error.message);
      navigate(generateLocaleRoute(AppRoute.SERVER_ERROR));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.error, toastError, navigate]);

  return null;
};
